import { HttpClient } from '@angular/common/http';
import {
  Component,
  Inject,
  Injectable,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { URLS } from '@shared-config/urls';
import { LoaderService } from '@shared-utils/loader/loader.service';
import { MatSnackBar, MatSnackBarAction, MatSnackBarActions, MatSnackBarLabel, MatSnackBarRef } from '@angular/material/snack-bar';
import { EMPTY, Observable, Subject, catchError, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BasicProcessRepsonse,
  ProcessResponse,
} from '@shared-interfaces/shared-process';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export type ProcessStatus = 'running' | 'stopped';

interface ProcessState {
  status: ProcessStatus;
  processes: BasicProcessRepsonse[];
}

@Injectable({
  providedIn: 'root',
})
export class ProcessService {
  private loaderService = inject(LoaderService);
  private snackBar = inject(MatSnackBar);
  private httpClient = inject(HttpClient);

  // state
  private processState = signal<ProcessState>({
    status: 'stopped',
    processes: [],
  });

  // sources && actions
  startPolling$ = new Subject<BasicProcessRepsonse>();
  stopPolling$ = new Subject<BasicProcessRepsonse>();

  startPollingRunningProcesses$ = new Subject<BasicProcessRepsonse[]>();
  stopPollingAll$ = new Subject<void>();

  cancelProcess$ = new Subject<string>();
  canceledProcess$ = this.cancelProcess$.pipe(
    switchMap((uid: string) =>
      this.cancelProcess(uid).pipe(
        catchError((err) => {
          this.openSnackBar();
          return EMPTY;
        }),
      ),
    ),
  );

  // Helping Actions
  private polling$ = new Subject<void>();
  private startApiCall$ = new Subject<void>();
  private triggerApiCall$ = this.startApiCall$.pipe(
    switchMap(() =>
      this.getProcessInformation().pipe(
        catchError((err) => {
          this.openSnackBar();
          return EMPTY;
        }),
      ),
    ),
  );

  // selectors
  processes = computed(() => this.processState().processes);
  status = computed(() => this.processState().status);

  // source
  processResults$ = new Subject<ProcessResponse>();
  processRunning$ = new Subject<ProcessResponse>();

  private durationInSeconds: number = 3;

  constructor() {
        this.startPolling$.pipe(takeUntilDestroyed()).subscribe({
      next: (basicProcess: BasicProcessRepsonse) => {
        this.processState.update((state: ProcessState) => ({
          ...state,
          processes: [...state.processes, basicProcess],
        }));
        this.polling$.next();
      },
    });

    this.stopPolling$.pipe(takeUntilDestroyed()).subscribe({
      next: (basicProcess: BasicProcessRepsonse) => {
        this.processState.update((state: ProcessState) => ({
          ...state,
          processes: state.processes.filter(
            (proc) => proc.uid != basicProcess.uid,
          ),
        }));
        this.loaderService.hide();
      },
    });

    this.startPollingRunningProcesses$.pipe(takeUntilDestroyed()).subscribe({
      next: (runningProcesses: BasicProcessRepsonse[]) => {
        this.processState.update((state: ProcessState) => ({
          ...state,
          processes: [...state.processes, ...runningProcesses],
        }));
        this.processes().map(() => this.loaderService.show());
        this.polling$.next();
      },
    });

    this.stopPollingAll$.pipe(takeUntilDestroyed()).subscribe({
      next: () => {
        this.processes().map(() => this.loaderService.hide());
        this.processState.update((state: ProcessState) => ({
          status: 'stopped',
          processes: [],
        }));
      },
    });

    this.polling$.pipe(takeUntilDestroyed()).subscribe({
      next: () => {
        if (this.status() != 'running') {
          this.processState.update((state: ProcessState) => ({
            ...state,
            status: 'running'
          }));
          this.startApiCall$.next();
        }
      },
    });

    this.canceledProcess$.pipe(takeUntilDestroyed()).subscribe({
      next: (response: any) => {},
    });

    this.triggerApiCall$.pipe(takeUntilDestroyed()).subscribe({
      next: (processResults: ProcessResponse[]) => {
        processResults.map((proc) => this.processActions(proc));
        setTimeout(() => {
          if (this.processes().length == 0) {
            this.stopPollingAll$.next();
          } else {
            this.startApiCall$.next();
          }
        }, 5000);
      },
    });
  }

  processActions(process: ProcessResponse): void {
    switch (process.status) {
      case 'completed':
      case 'cancelled':
        this.processResults$.next(process);
        this.stopPolling$.next(process);
        break;
      case 'failed':
        this.processResults$.next(process);
        this.stopPolling$.next(process);
        this.openSnackBar();
        break;
      case 'running':
        this.processRunning$.next(process);
        break;
      default:
        break;
    }
  }

  private getProcessInformation(): Observable<ProcessResponse[]> {
    const processIds = {
      processIds: this.processes().map((proc) => proc.uid),
    };
    return this.httpClient.post<ProcessResponse[]>(URLS.PROCESS, processIds);
  }

  private cancelProcess(processId: string): Observable<ProcessResponse[]> {
    return this.httpClient.delete<ProcessResponse[]>(
      `${URLS.PROCESS}/${processId}`,
    );
  }

  openSnackBar() {
    this.snackBar.openFromComponent(SnackBarComponent, {
      // No duration - stays until manually dismissed
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.stopPollingAll$.next()
  }
}


@Component({
  selector: 'snack-bar',
  templateUrl: './snack-bar.html',
  styles: `
    :host {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: #DA291C;
      border-radius: 8px;
      min-width: 350px;
      padding: 16px;
      gap: 16px;
    }

    .snack-bar-content {
      flex: 1;
    }

    .snack-bar-message {
      color: white;
      
      strong {
        display: block;
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 4px;
      }
      
      p {
        margin: 0;
        font-size: 14px;
        line-height: 1.4;
      }
    }

    .snack-bar-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      
      button {
        color: white;
        border-color: white;
      }
      
      mat-icon {
        cursor: pointer;
        color: white;
        
        &:hover {
          opacity: 0.8;
        }
      }
    }
  `,
  standalone: true,
  imports: [
    MatButtonModule,
    MatSnackBarLabel,
    MatSnackBarActions,
    MatSnackBarAction,
    MatIconModule,
  ],
})
export class SnackBarComponent {
  snackBarRef = inject(MatSnackBarRef);
}